import {
  POSTER_PAGE,
  type PosterDoc,
  type PosterElement,
  type PosterImageSource,
  type PosterShapeKind,
  type PosterStickerKind,
  type PosterTextElement,
} from "@studio/shared";
import { DEFAULT_BODY_FONT, DEFAULT_TITLE_FONT } from "./fonts";
import { QR_MIN_MM } from "./geometry";

/**
 * 포스터 편집기의 «순수» 동작 — 화면 없이 시험할 수 있게 여기 모은다.
 * 좌표는 전부 mm(A3 297×420). 목업(400px 폭)의 숫자는 ×0.7425 로 옮겼다.
 */

export type TextPreset = "title" | "sub" | "body" | "info";

const TEXT_PRESETS: Record<TextPreset, Pick<PosterTextElement, "text" | "font" | "size" | "weight" | "w">> = {
  title: { text: "제목을 써요", font: DEFAULT_TITLE_FONT, size: 84, weight: 400, w: 223 },
  sub: { text: "부제목을 써요", font: DEFAULT_BODY_FONT, size: 42, weight: 700, w: 193 },
  body: { text: "설명 글을 써요.\n두 번 눌러 고칠 수 있어요.", font: "gowun-dodum", size: 27, weight: 400, w: 178 },
  info: { text: "10월 24일 – 10월 31일\n본관 1층", font: DEFAULT_BODY_FONT, size: 25, weight: 700, w: 149 },
};

export function newId(): string {
  return `e${Math.random().toString(36).slice(2, 10)}`;
}

export function textElement(preset: TextPreset): PosterTextElement {
  const value = TEXT_PRESETS[preset];
  return {
    id: newId(),
    type: "text",
    ...value,
    color: "#1c1917",
    align: "left",
    x: (POSTER_PAGE.width - value.w) / 2,
    y: 178,
    rot: 0,
    opacity: 1,
  };
}

export function imageElement(src: PosterImageSource, aspect = 1): PosterElement {
  const w = 134;
  const h = Math.round(w / Math.max(0.2, Math.min(5, aspect)));
  return { id: newId(), type: "image", src, x: (POSTER_PAGE.width - w) / 2, y: 126, w, h, rot: 0, opacity: 1, shape: aspect === 1 ? "round" : "rect", border: "none" };
}

export function shapeElement(kind: PosterShapeKind): PosterElement {
  const w = kind === "line" ? 149 : 104;
  const h = kind === "line" ? 2 : kind === "pill" ? 25 : 82;
  return { id: newId(), type: "shape", kind, x: (POSTER_PAGE.width - w) / 2, y: 178, w, h, rot: 0, opacity: 1, fill: "#e0703a", stroke: "none" };
}

export function stickerElement(kind: PosterStickerKind): PosterElement {
  return { id: newId(), type: "sticker", kind, color: "#e0703a", x: 126, y: 186, w: 48, h: 48, rot: 0, opacity: 1 };
}

export function addElement(doc: PosterDoc, element: PosterElement): PosterDoc {
  return { ...doc, els: [...doc.els, element] };
}

export function updateElement(doc: PosterDoc, id: string, patch: Partial<PosterElement>): PosterDoc {
  return {
    ...doc,
    els: doc.els.map((element) => element.id === id ? clampQr({ ...element, ...patch } as PosterElement) : element),
  };
}

/** QR 은 인쇄 3cm 보다 작아지지 않고, 돌아가지 않는다(인식률). */
export function clampQr(element: PosterElement): PosterElement {
  if (element.type !== "qr") return element;
  const size = Math.max(QR_MIN_MM, element.w);
  return { ...element, w: size, h: size, rot: 0 };
}

export type RemoveResult = { doc: PosterDoc; removed: true } | { doc: PosterDoc; removed: false; reason: "qr" };

/** 🚨 QR 은 지울 수 없다(입력 문서 2단계 — AR 을 여는 유일한 입구). */
export function removeElement(doc: PosterDoc, id: string): RemoveResult {
  const target = doc.els.find((element) => element.id === id);
  if (!target) return { doc, removed: true };
  if (target.type === "qr") return { doc, removed: false, reason: "qr" };
  return { doc: { ...doc, els: doc.els.filter((element) => element.id !== id) }, removed: true };
}

export function duplicateElement(doc: PosterDoc, id: string): { doc: PosterDoc; id: string | null } {
  const index = doc.els.findIndex((element) => element.id === id);
  const source = doc.els[index];
  // QR 은 하나만 — 복제하면 어느 쪽이 진짜인지 학생이 헷갈린다.
  if (!source || source.type === "qr") return { doc, id: null };
  const copy = { ...structuredClone(source), id: newId(), x: source.x + 10, y: source.y + 10 } as PosterElement;
  const els = [...doc.els];
  els.splice(index + 1, 0, copy);
  return { doc: { ...doc, els }, id: copy.id };
}

export type OrderAction = "front" | "forward" | "back";

export function reorderElement(doc: PosterDoc, id: string, action: OrderAction): PosterDoc {
  const index = doc.els.findIndex((element) => element.id === id);
  if (index < 0) return doc;
  const els = [...doc.els];
  const [element] = els.splice(index, 1);
  if (action === "front") els.push(element!);
  else if (action === "back") els.unshift(element!);
  else els.splice(Math.min(els.length, index + 1), 0, element!);
  return { ...doc, els };
}

/**
 * 모서리 손잡이로 크기 바꾸기. 글상자는 폭과 글자 크기를 같이 키운다(목업과 같다).
 * 도형은 가로·세로 따로(Shift 면 비율 유지), 나머지는 비율 유지.
 */
export function resizeElement(element: PosterElement, dxMm: number, dyMm: number, keepRatio: boolean, origin: PosterElement): PosterElement {
  if (origin.type === "text") {
    const k = Math.max(0.3, (origin.w + dxMm) / origin.w);
    return { ...(element as PosterTextElement), w: Math.max(20, origin.w * k), size: Math.max(6, Math.round(origin.size * k * 10) / 10) };
  }
  const originH = (origin as { h: number }).h;
  if (origin.type === "shape" && !keepRatio) {
    return { ...element, w: Math.max(4, origin.w + dxMm), h: Math.max(origin.kind === "line" ? 0.5 : 4, originH + dyMm) } as PosterElement;
  }
  const min = origin.type === "qr" ? QR_MIN_MM / origin.w : 0.1;
  const k = Math.max(min, (origin.w + dxMm) / origin.w);
  return clampQr({ ...element, w: origin.w * k, h: originH * k } as PosterElement);
}

/** 회전 — 0·±90·180 근처(5°)에서 붙는다. Shift 면 안 붙는다. */
export function snapRotation(degrees: number, free: boolean): number {
  let angle = ((degrees + 540) % 360) - 180;
  if (!free) {
    for (const snap of [0, 90, -90, 180, -180]) if (Math.abs(angle - snap) < 5) angle = snap;
  }
  return Math.round(angle);
}

/** 실행 취소 기록(최대 100칸) */
export class History {
  #past: string[] = [];
  #future: string[] = [];

  push(before: PosterDoc): void {
    this.#past.push(JSON.stringify(before));
    if (this.#past.length > 100) this.#past.shift();
    this.#future = [];
  }

  undo(current: PosterDoc): PosterDoc | null {
    const previous = this.#past.pop();
    if (!previous) return null;
    this.#future.push(JSON.stringify(current));
    return JSON.parse(previous) as PosterDoc;
  }

  redo(current: PosterDoc): PosterDoc | null {
    const next = this.#future.pop();
    if (!next) return null;
    this.#past.push(JSON.stringify(current));
    return JSON.parse(next) as PosterDoc;
  }

  get canUndo(): boolean {
    return this.#past.length > 0;
  }

  get canRedo(): boolean {
    return this.#future.length > 0;
  }
}
